/**
 * Backing-store resolution for editor canvases.
 *
 * Every render, every object cache and every pixel read fills the whole
 * backing store, and whatever the screen cannot show is averaged away by the
 * browser's downscale. A mosaic card's store follows the zoom in steps: the
 * smallest step that still stores a pixel for every device pixel, up to the
 * retina store once the zoom needs more than the design size
 * (`fabricBackingScalePatch` lets Fabric size a store below 1x).
 *
 * Variant cells are already drawn at their cell size and only switch between
 * retina and 1x.
 */
import { config as fabricConfig, type StaticCanvas } from 'fabric';

/** Backing-store scales a card steps through below its design size. */
const BACKING_STEPS = [0.125, 0.25, 0.375, 0.5, 0.75, 1];

/**
 * How far a store may exceed what the zoom needs before it steps down. Each
 * step resizes every card and redraws its object caches, so a zoom that
 * settles just past a step does not pay that on every nudge across it.
 */
const STEP_DOWN_MARGIN = 1.6;

/**
 * The backing-store scale for a card shown at this zoom. `current` is the
 * card's present scale, or null before any card is built.
 */
export function backingScaleForZoom(
  zoom: number,
  current: number | null
): number {
  const needed = zoom * (window.devicePixelRatio || 1);
  if (
    current !== null &&
    current >= needed &&
    current <= needed * STEP_DOWN_MARGIN
  ) {
    return current;
  }
  return BACKING_STEPS.find(step => step >= needed) ?? retinaScale();
}

/** The store a capture sampling the live canvas needs: the retina one. */
export function fullBackingScale(): number {
  return retinaScale();
}

/**
 * Size a canvas's backing store at `scale` of its design size. Returns true
 * when it was actually resized.
 */
export function applyBackingScale(
  canvas: StaticCanvas,
  scale: number
): boolean {
  if (canvas.enableRetinaScaling && canvas.backingScale === scale) return false;
  canvas.enableRetinaScaling = true;
  canvas.backingScale = scale;
  canvas.setDimensions({ width: canvas.width, height: canvas.height });
  return true;
}

/**
 * How far past the switch point zoom must climb before retina comes back.
 * Each edge resizes every card's store — and on a retina display rebuilds
 * their object caches with it — so the edges sit apart: a zoom that settled
 * right on a single threshold would pay that on every nudge across it.
 */
const RETINA_HYSTERESIS = 1.25;

/**
 * Decide whether a canvas shown at this zoom should keep its retina backing
 * store. `current` is the canvas's present setting and picks which edge of
 * the hysteresis band applies.
 */
export function shouldEnableRetina(zoom: number, current: boolean): boolean {
  // A 1x display renders both settings identically, so switching off would
  // resize every card and change no pixel.
  if (!retinaChangesPixels()) return true;

  const switchZoom = oneToOneZoom();
  return current ? zoom > switchZoom : zoom > switchZoom * RETINA_HYSTERESIS;
}

/**
 * Switch a canvas between a retina and a 1x backing store. Fabric only reads
 * `enableRetinaScaling` while sizing the elements, so the dimensions have to
 * be re-applied for a change to take effect. No-op when the canvas is already
 * in the wanted state; returns true when it was actually resized.
 */
export function applyRetinaScaling(
  canvas: StaticCanvas,
  enabled: boolean
): boolean {
  if (canvas.enableRetinaScaling === enabled) return false;
  canvas.enableRetinaScaling = enabled;

  // Record the flag either way, but skip the resize when both settings give
  // the same store — exports force retina on 1x displays too, and setting the
  // dimensions clears the bitmap and repaints even when nothing changed.
  if (!retinaChangesPixels()) return false;

  canvas.setDimensions({ width: canvas.width, height: canvas.height });
  return true;
}

/**
 * Helpers
 */

/**
 * Zoom at which one canvas pixel already covers one device pixel. Below it a
 * 1x store still carries at least one stored pixel per device pixel, so retina
 * only adds supersampling. 0.5 on a 2x display, 1 on a plain one.
 */
function oneToOneZoom(): number {
  return 1 / Math.max(window.devicePixelRatio || 1, 1);
}

/** False on a 1x display, where Fabric renders both settings identically. */
function retinaChangesPixels(): boolean {
  return (window.devicePixelRatio || 1) > 1;
}

/** Fabric's device ratio, capped at app start. */
function retinaScale(): number {
  return Math.max(fabricConfig.devicePixelRatio, 1);
}
